// src/App.js

import React from 'react';

import {

  BrowserRouter as Router,

  Routes,

  Route,

  Navigate,

  Link,

  useLocation,

  useNavigate

} from 'react-router-dom';

import edfLogo from './assets/edf-purview-logo-transparent.png';

// Import pages

import Dashboard from './pages/Dashboard';

import Cabling from './pages/Cabling';

import Reports from './pages/Reports';

import Inventory from './pages/Inventory';

import DOA from './pages/DOA';

import RackElevation from './pages/RackElevation';

import Login from './pages/Login';

import { config } from './config';

// Dynamic Digital Twin Component Loader

function DynamicDigitalTwin({ version }) {

  let Component = null;

  if (version === 'digitaltwin1') {

    const Module = require('./components/digitaltwin1');

    Component = Module.SharonS6Scene || Module.default;

  } else {

    const Module = require('./components/digitaltwin');

    Component = Module.DataCenterScene || Module.default;

  }

  return Component ? <Component /> : <div>Loading...</div>;

}

// Font constant for consistency

const FONT_FAMILY = 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Loading Spinner component

function LoadingSpinner({

  title = 'Loading',

  subtitle = 'Please wait while we fetch your data...'

}) {

  return (

    <div

      style={{

        position: 'fixed',

        inset: 0,

        background: 'rgba(255,255,255,0.95)',

        display: 'flex',

        flexDirection: 'column',

        alignItems: 'center',

        justifyContent: 'center',

        zIndex: 9999,

        backdropFilter: 'blur(4px)',

        fontFamily: FONT_FAMILY,

      }}

    >

      <div

        style={{

          position: 'relative',

          width: 80,

          height: 80,

          marginBottom: 24

        }}

      >

        <svg

          width="80"

          height="80"

          viewBox="0 0 80 80"

          style={{ position: 'absolute', animation: 'spin 2s linear infinite' }}

        >

          <circle cx="40" cy="40" r="35" fill="none" stroke="#e2e8f0" strokeWidth="2" />

          <circle

            cx="40"

            cy="40"

            r="35"

            fill="none"

            stroke="#1e40af"

            strokeWidth="2"

            strokeDasharray="55 165"

            strokeLinecap="round"

          />

        </svg>

        <svg

          width="80"

          height="80"

          viewBox="0 0 80 80"

          style={{ position: 'absolute', animation: 'spinReverse 3s linear infinite' }}

        >

          <circle cx="40" cy="40" r="25" fill="none" stroke="#f1f5f9" strokeWidth="1.5" />

          <circle

            cx="40"

            cy="40"

            r="25"

            fill="none"

            stroke="#3b82f6"

            strokeWidth="1.5"

            strokeDasharray="39 78"

            strokeLinecap="round"

          />

        </svg>

        <div

          style={{

            position: 'absolute',

            inset: 0,

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'center'

          }}

        >

          <div

            style={{

              width: 12,

              height: 12,

              borderRadius: '50%',

              background: '#1e40af',

              opacity: 0.8

            }}

          />

        </div>

      </div>

      <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f172a' }}>

        {title}

      </p>

      <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#64748b', fontWeight: 500 }}>

        {subtitle}

      </p>

      <style>{`

        @keyframes spin {

          from { transform: rotate(0deg); }

          to { transform: rotate(360deg); }

        }

        @keyframes spinReverse {

          from { transform: rotate(0deg); }

          to { transform: rotate(-360deg); }

        }

      `}</style>

    </div>

  );

}

// Protected Route wrapper

function ProtectedRoute({ children }) {

  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

  const location = useLocation();

  if (!isAuthenticated) {

    return <Navigate to="/login" state={{ from: location }} replace />;

  }

  return children;

}

// App Header with Logout - Added font-family

function AppHeader({ digitalTwinVersion, onDigitalTwinChange }) {

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const handleLogout = () => {

    localStorage.removeItem('isAuthenticated');

    localStorage.removeItem('user');

    navigate('/login');

  };

  const handleVersionChange = (version) => {

    onDigitalTwinChange(version);

  };

  return (

    <div

      style={{

        position: 'fixed',

        top: 0,

        left: 0,

        right: 0,

        zIndex: 1001,

        height: 56,

        background: 'linear-gradient(135deg, #0B1F3A 0%, #1e3a5f 100%)',

        display: 'flex',

        alignItems: 'center',

        justifyContent: 'space-between',

        padding: '0 24px',

        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',

        fontFamily: FONT_FAMILY,

      }}

    >

      {/* Left - Logo */}

      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>

        <img

          src={edfLogo}

          alt="EDF Purview Logo"

          style={{

            width: 120,

            height: 40,

            objectFit: 'contain',

            display: 'block',

            flexShrink: 0,

            filter: 'brightness(0) invert(1)',

          }}

        />

        {/* Divider + tagline */}

        <div style={{

          width: 1,

          height: 32,

          background: 'rgba(255,255,255,0.2)',

          marginLeft: 8,

          marginRight: 8,

          flexShrink: 0,

        }} />

        {/* Sharon Version Selector */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Sharon S3 (EDFPurview) - Active deployment version */}
          <button
            onClick={() => handleVersionChange('digitaltwin')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: digitalTwinVersion === 'digitaltwin'
                ? 'rgba(99, 179, 237, 0.4)'
                : 'rgba(255,255,255,0.1)',
              border: '1px solid ' + (digitalTwinVersion === 'digitaltwin'
                ? 'rgba(99, 179, 237, 0.8)'
                : 'rgba(255,255,255,0.2)'),
              color: '#ffffff',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              letterSpacing: '0.3px',
            }}
            onMouseEnter={(e) => {
              if (digitalTwinVersion !== 'digitaltwin') {
                e.target.style.background = 'rgba(255,255,255,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (digitalTwinVersion !== 'digitaltwin') {
                e.target.style.background = 'rgba(255,255,255,0.1)';
              }
            }}
          >
            Sharon S3
          </button>

          {/* Sharon S6 (PurviewProjects) - Inactive for S3 deployment
          <button
            onClick={() => handleVersionChange('digitaltwin1')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: digitalTwinVersion === 'digitaltwin1'
                ? 'rgba(99, 179, 237, 0.4)'
                : 'rgba(255,255,255,0.1)',
              border: '1px solid ' + (digitalTwinVersion === 'digitaltwin1'
                ? 'rgba(99, 179, 237, 0.8)'
                : 'rgba(255,255,255,0.2)'),
              color: '#ffffff',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              letterSpacing: '0.3px',
            }}
            onMouseEnter={(e) => {
              if (digitalTwinVersion !== 'digitaltwin1') {
                e.target.style.background = 'rgba(255,255,255,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (digitalTwinVersion !== 'digitaltwin1') {
                e.target.style.background = 'rgba(255,255,255,0.1)';
              }
            }}
          >
            Sharon S6
          </button>
          */}
        </div>
      </div>

      {/* Center - Title */}

      <div style={{ textAlign: 'center' }}>

        <div

          style={{

            fontSize: 16,

            fontWeight: 800,

            color: '#ffffff',

            letterSpacing: '-0.3px',

            lineHeight: 1.3,

          }}

        >

          Data Center Deployer

        </div>

        <div

          style={{

            fontSize: 10,

            color: 'rgba(255,255,255,0.7)',

            letterSpacing: '0.3px',

            marginTop: 4,

            lineHeight: 1.2,

          }}

        >

          your DC Delivery Solution

        </div>

      </div>

      {/* Right - User Info & Logout with SVG Icon */}

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>

        {user && (

          <div style={{ textAlign: 'right' }}>

            <div

              style={{

                fontSize: 12,

                fontWeight: 600,

                color: '#ffffff',

              }}

            >

              {user.username}

            </div>

            <div

              style={{

                fontSize: 10,

                color: 'rgba(255,255,255,0.6)',

              }}

            >

              {user.role} • {user.department || 'N/A'}

            </div>

          </div>

        )}

        <button

          onClick={handleLogout}

          style={{

            padding: '6px 16px',

            borderRadius: 8,

            background: 'rgba(239, 68, 68, 0.15)',

            border: '1px solid rgba(239, 68, 68, 0.3)',

            color: '#ffffff',

            cursor: 'pointer',

            fontSize: 12,

            fontWeight: 600,

            transition: 'all 0.2s',

            display: 'flex',

            alignItems: 'center',

            gap: '6px',

            fontFamily: 'inherit',

          }}

          onMouseEnter={(e) => {

            e.target.style.background = 'rgba(239, 68, 68, 0.25)';

            e.target.style.transform = 'scale(1.05)';

          }}

          onMouseLeave={(e) => {

            e.target.style.background = 'rgba(239, 68, 68, 0.15)';

            e.target.style.transform = 'scale(1)';

          }}

        >

          {/* SVG Logout Icon */}

          <svg

            width="14"

            height="14"

            viewBox="0 0 24 24"

            fill="none"

            stroke="currentColor"

            strokeWidth="2"

            strokeLinecap="round"

            strokeLinejoin="round"

          >

            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />

            <polyline points="16 17 21 12 16 7" />

            <line x1="21" y1="12" x2="9" y2="12" />

          </svg>

          Logout

        </button>

      </div>

    </div>

  );

}

// NavTabs - Added font-family

function NavTabs() {

  const location = useLocation();

  const navItems = [

    { path: '/digitaltwin', label: 'Digital Twin', icon: 'digitaltwin' },

    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },

    { path: '/reports', label: 'Unloading Dock', icon: 'reports' },

    { path: '/inventory', label: 'Inventory', icon: 'inventory' },

    { path: '/cabling', label: 'Cabling', icon: 'cabling' },

    { path: '/doa', label: 'DOA', icon: 'doa' },

    { path: '/rackelevation', label: 'Rack Elevation', icon: 'rack elevation' }

  ];

  const getIcon = (iconType) => {

    const iconProps = {

      width: '18',

      height: '18',

      fill: 'none',

      stroke: 'currentColor',

      strokeWidth: '2',

      strokeLinecap: 'round',

      strokeLinejoin: 'round',

      viewBox: '0 0 24 24'

    };

    const icons = {

      dashboard: (

        <svg {...iconProps}>

          <path d="M3 3v18h18" />

          <path d="M18 17V9" />

          <path d="M13 17V5" />

          <path d="M8 17v-7" />

        </svg>

      ),

      cabling: (

        <svg {...iconProps}>

          <path d="M4 4h16v16H4z" />

          <path d="M8 8h8" />

          <path d="M8 12h8" />

          <path d="M8 16h8" />

          <circle cx="12" cy="20" r="1" />

          <circle cx="12" cy="4" r="1" />

          <path d="M12 8v8" />

        </svg>

      ),

      reports: (

        <svg {...iconProps}>

          <path d="M4 19.5V4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5Z" />

          <path d="M8 7h8" />

          <path d="M8 11h8" />

          <path d="M8 15h5" />

        </svg>

      ),

      inventory: (

        <svg {...iconProps}>

          <rect x="3" y="4" width="18" height="18" rx="2" />

          <path d="M7 8h10" />

          <path d="M7 12h10" />

          <path d="M7 16h6" />

        </svg>

      ),

      doa: (

        <svg {...iconProps}>

          <circle cx="12" cy="12" r="10" />

          <path d="M12 8v4" />

          <path d="M12 16h.01" />

        </svg>

      ),

      'rack elevation': (

        <svg {...iconProps}>

          <rect x="5" y="2" width="14" height="18" rx="2" />

          <rect x="3" y="20" width="18" height="2" rx="1" />

          <path d="M5 7h14" />

          <path d="M5 12h14" />

          <path d="M5 17h14" />

        </svg>

      ),

      digitaltwin: (

        <svg {...iconProps}>

          <rect x="3" y="3" width="18" height="18" rx="2" />

          <path d="M3 9h18" />

          <path d="M9 21V9" />

          <circle cx="15" cy="15" r="2" />

        </svg>

      )

    };

    return icons[iconType];

  };

  const isActive = (path) => location.pathname === path;

  return (

    <nav

      style={{

        position: 'fixed',

        top: 56,

        left: 0,

        right: 0,

        zIndex: 1000,

        width: '100%',

        height: 48,

        background: '#ffffff',

        borderBottom: '1px solid #e2e8f0',

        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',

        fontFamily: FONT_FAMILY,

      }}

    >

      <div

        style={{

          display: 'flex',

          alignItems: 'center',

          justifyContent: 'space-between',

          height: '48px',

          padding: '0 24px',

          width: '100%',

          gap: '8px',

          overflowX: 'auto',

          overflowY: 'hidden',

          scrollbarWidth: 'thin',

          scrollbarColor: '#cbd5e1 transparent',

        }}

      >

        {navItems.map((item) => (

          <Link

            key={item.path}

            to={item.path}

            title={item.label}

            style={{

              display: 'flex',

              alignItems: 'center',

              justifyContent: 'center',

              gap: '6px',

              padding: '6px 14px',

              textDecoration: 'none',

              color: isActive(item.path) ? '#1e40af' : '#64748b',

              background: isActive(item.path) ? 'rgba(37,99,235,0.08)' : 'transparent',

              fontWeight: isActive(item.path) ? '600' : '500',

              fontSize: '13px',

              borderRadius: '8px',

              whiteSpace: 'nowrap',

              cursor: 'pointer',

              flex: 1,

              textAlign: 'center',

              transition: 'all 0.2s ease',

              fontFamily: 'inherit',

            }}

          >

            <span style={{ flexShrink: 0 }}>{getIcon(item.icon)}</span>

            <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>

          </Link>

        ))}

      </div>

    </nav>

  );

}

function App() {

  // The deployment environment selects the initial digital twin.

  const [digitalTwinVersion, setDigitalTwinVersion] = React.useState(

    config.DIGITAL_TWIN_VERSION

  );

  // Handle digital twin version change

  const handleDigitalTwinChange = (newVersion) => {

    setDigitalTwinVersion(newVersion);

  };

  return (

    <Router>

      <Routes>

        {/* Public route - Login */}

        <Route path="/login" element={<Login />} />

        {/* Protected routes */}

        <Route

          path="/*"

          element={

            <ProtectedRoute>

              <div

                style={{

                  width: '100%',

                  height: '100vh',

                  overflow: 'hidden',

                  background: '#f8fafc',

                  display: 'flex',

                  flexDirection: 'column',

                  paddingTop: '104px',

                  fontFamily: FONT_FAMILY,

                }}

              >

                <AppHeader 

                  digitalTwinVersion={digitalTwinVersion}

                  onDigitalTwinChange={handleDigitalTwinChange}

                />

                <NavTabs />

                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>

                  <Routes>

                    <Route path="/digitaltwin" element={

                      <div style={{ height: '100%', overflow: 'hidden' }}>

                        <DynamicDigitalTwin version={digitalTwinVersion} />

                      </div>

                    } />

                    <Route path="/dashboard" element={<Dashboard />} />

                    <Route path="/reports" element={<Reports />} />

                    <Route path="/inventory" element={<Inventory />} />

                    <Route path="/cabling" element={<Cabling />} />

                    <Route path="/doa" element={<DOA />} />

                    <Route path="/rackelevation" element={<RackElevation />} />

                    <Route path="/" element={<Navigate to="/digitaltwin" replace />} />

                  </Routes>

                </div>

              </div>

            </ProtectedRoute>

          }

        />

      </Routes>

    </Router>

  );

}

export { LoadingSpinner };

export default App;